import { inject, Injectable } from '@angular/core';
import { CartRow, CartService, ProductService } from 'gung-standard';
import { first, forkJoin, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ColmecCartService extends CartService {
  private productService = inject(ProductService);

  private roles: string[];
  private maxQty: number;

  constructor() {
    super();

    this.authService
      .getCurrentUser()
      .pipe()
      .subscribe(user => {
        if (user) {
          this.roles = user.roles;
          if (this.roles.length === 1 && this.roles.indexOf('USER') === 1) {
            this.maxQty = 50;
          } else {
            this.maxQty = 50;
          }
        }
      });
  }

  addToCart(productId: string, qty: number, targetStockId?: string) {
    this.getProductQty(productId)
      .pipe(first())
      .subscribe(qtyCurrent => {
        if (qtyCurrent + qty <= this.maxQty) {
          super.addToCart(productId, qty, targetStockId);

          this.setProductRelatedConfiguration(productId);
        }
      });
  }

  setProductQuantity(productId: string, qty: number, targetStockId?: string) {
    if (qty > this.maxQty) {
      qty = this.maxQty;
    }
    super.setProductQuantity(productId, qty, targetStockId);
  }

  setConfiguredRim(rimId: string, productId: string): void {
    this.getCartRowByProductId(productId).subscribe(cartRow => {
      if (cartRow) {
        let cartRowExtra = cartRow.extra;

        if (rimId) {
          cartRowExtra = {
            ...cartRowExtra,
            configure: {
              ...(cartRowExtra.configure || {}),
              rim: rimId
            }
          };
        } else {
          if (cartRowExtra.configure && cartRowExtra.configure.rim) {
            const { rim, ...configureWithoutRim } = cartRowExtra.configure;
            cartRowExtra.configure = configureWithoutRim;
          }
        }

        this.setExtra(productId, cartRowExtra);
      }
    });
  }

  setConfiguredAssembly(assemblyId: string, productId: string): void {
    this.getCartRowByProductId(productId).subscribe(cartRow => {
      if (cartRow) {
        const cartRowExtra = cartRow.extra;

        if (assemblyId) {
          if (cartRowExtra.configure) {
            cartRowExtra.configure.assembly = assemblyId;
          } else {
            cartRowExtra.configure = { assembly: assemblyId };
          }
        } else {
          if (cartRowExtra.configure && cartRowExtra.configure.assembly) {
            const { assembly, ...configureWithoutAssembly } = cartRowExtra.configure;
            cartRowExtra.configure = configureWithoutAssembly;
          }
        }

        this.setExtra(productId, cartRowExtra);
      }
    });
  }

  setConfiguredAutoAdded(autoAddedId: string, productId: string): void {
    this.getCartRowByProductId(productId).subscribe(cartRow => {
      if (cartRow) {
        const cartRowExtra = cartRow.extra;

        if (autoAddedId) {
          if (cartRowExtra.configure) {
            cartRowExtra.configure.autoAdded = autoAddedId;
          } else {
            cartRowExtra.configure = { autoAdded: autoAddedId };
          }
        } else {
          if (cartRowExtra.configure && cartRowExtra.configure.autoAdded) {
            const { autoAdded, ...configureWithoutAutoAdded } = cartRowExtra.configure;
            cartRowExtra.configure = configureWithoutAutoAdded;
          }
        }

        this.setExtra(productId, cartRowExtra);
      }
    });
  }

  setConfiguredRecyclingCharge(artnraddartId: string, productId: string): void {
    this.getCartRowByProductId(productId).subscribe(cartRow => {
      if (cartRow) {
        const cartRowExtra = cartRow.extra;

        if (artnraddartId) {
          if (cartRowExtra.configure) {
            cartRowExtra.configure.artnraddart = artnraddartId;
          } else {
            cartRowExtra.configure = { artnraddart: artnraddartId };
          }
        } else {
          if (cartRowExtra.configure && cartRowExtra.configure.artnraddart) {
            const { artnraddart, ...configureWithoutArtnraddart } = cartRowExtra.configure;
            cartRowExtra.configure = configureWithoutArtnraddart;
          }
        }

        this.setExtra(productId, cartRowExtra);
      }
    });
  }

  setConfiguredAddon(addonId: string, productId: string): void {
    this.getCartRowByProductId(productId).subscribe(cartRow => {
      if (cartRow) {
        const cartRowExtra = cartRow.extra;

        if (addonId) {
          if (cartRowExtra.configure) {
            cartRowExtra.configure.addon = addonId;
          } else {
            cartRowExtra.configure = { addon: addonId };
          }
        } else {
          if (cartRowExtra.configure && cartRowExtra.configure.addon) {
            const { addon, ...configureWithoutAddon } = cartRowExtra.configure;
            cartRowExtra.configure = configureWithoutAddon;
          }
        }

        this.setExtra(productId, cartRowExtra);
      }
    });
  }

  private getCartRowByProductId(productId: string): Observable<CartRow> {
    return this.getCurrentCart().pipe(
      first(),
      map(cartRows => cartRows.find(row => row.productId === productId))
    );
  }

  public setProductRelatedConfiguration(productId: string): void {
    // GET cart row being added/updated and product info
    // AND set the configure entries related with the product if not set yet

    // THE RELATED CONFIGURATIONS ARE: AutoAdded (product.extra.ar.tillvalartikelauto) and Recycling Charge (product.extra.ar.artnraddart)

    //    IF product.extra.ar.tillvalartikelauto (means add auto added) and cart row config autoAdded is not set yet
    //        THEN set current cart row extra configure autoAdded with the artnrtillvalart of the product.extra.artv

    //    IF product.extra.ar.artnraddart (means it has a recycling charge) and cart row config artnraddart is not set yet
    //        THEN set current cart row extra configure artnraddart with product.extra.ar.artnraddart

    forkJoin({
      cartRow: this.getCartRowByProductId(productId).pipe(first()),
      product: this.productService.getProduct(productId).pipe(first())
    }).subscribe(result => {
      // autoAdded
      if (result.product.extra.ar.tillvalartikelauto) {
        if (!(result.cartRow.extra.configure && result.cartRow.extra.configure.autoAdded)) {
          result.product.extra.artv.forEach(artv => {
            this.setConfiguredAutoAdded(artv.artnrtillvalart, productId);
          });
        }
      }

      // Recycling Charge
      if (result.product.extra.ar.artnraddart) {
        if (!(result.cartRow.extra.configure && result.cartRow.extra.configure.artnraddart)) {
          this.setConfiguredRecyclingCharge(result.product.extra.ar.artnraddart, productId);
        }
      }
    });
  }
}
